import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Receipt } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    client_code: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: k === "client_code" ? e.target.value.toUpperCase() : e.target.value,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await register({ ...form, client_code: form.client_code.trim().toUpperCase() });
      toast.success(`Welcome, ${u.name}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 sm:p-10">
      <Card className="w-full max-w-lg border-slate-200 shadow-none">
        <CardContent className="p-8">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="register-brand-link">
            <div className="w-8 h-8 bg-slate-900 flex items-center justify-center">
              <Receipt className="w-4 h-4 text-white" />
            </div>
            <span className="font-display text-lg font-semibold tracking-tight text-slate-900">
              BILLING
            </span>
          </Link>

          <p className="label-tag mb-2">Create your account</p>
          <h1 className="font-display text-3xl font-medium text-slate-900 mb-2">
            Sign up as a client
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Enter the Client ID your provider shared with you (starts with{" "}
            <span className="font-mono text-slate-700">CID-</span>) to access your invoices.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label className="label-tag" htmlFor="client_code">Client ID</Label>
              <Input
                id="client_code"
                required
                placeholder="CID-0001"
                value={form.client_code}
                onChange={setField("client_code")}
                data-testid="register-client-code-input"
                className="rounded-md border-slate-300 font-mono tracking-wider"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-tag" htmlFor="name">Full name</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={setField("name")}
                data-testid="register-name-input"
                className="rounded-md border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-tag" htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={setField("email")}
                data-testid="register-email-input"
                className="rounded-md border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-tag" htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={setField("password")}
                data-testid="register-password-input"
                className="rounded-md border-slate-300"
              />
              <p className="text-xs text-slate-400">Minimum 6 characters.</p>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="register-submit-btn"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-md h-11"
            >
              {submitting ? "Creating…" : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-slate-900 font-medium underline underline-offset-4" data-testid="go-login-link">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
